import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { api, errorMessage } from '../lib/api';
import { clearUrlSecret, readUrlSecret } from '../lib/urlSecrets';
import { flag } from '../config';
import lazyAccountPage from '../components/site/auth/lazyAccountPage';

// SLOT_MODEL on: the redesigned page (site redesign Phase 6), loaded on demand.
const EmailPreferencesSite = lazyAccountPage(
  'EmailPreferencesSite',
  () => import(/* webpackChunkName: "account-pages" */ './account/EmailPreferencesSite'),
);

/** /email-preferences: the redesigned page with SLOT_MODEL on, the legacy page (unchanged) off. */
export default function EmailPreferences() {
  return flag('SLOT_MODEL') ? <EmailPreferencesSite /> : <EmailPreferencesLegacy />;
}

const EmailPreferencesLegacy = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // In the URL, or kept for this page by the index.html URL secret guard (lib/urlSecrets).
  const [keptToken] = useState(() => readUrlSecret('token', { location }));
  const token = searchParams.get('token') || keptToken;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [preferences, setPreferences] = useState(null);
  
  useEffect(() => {
    if (!token) {
      setError('Invalid or missing token');
      setLoading(false);
      return;
    }
    
    fetchPreferences();
  }, [token]);
  
  const fetchPreferences = async () => {
    try {
      const response = await api.get(`/email-preferences/${encodeURIComponent(token)}`);
      setPreferences(response.data);
      setLoading(false);
    } catch (err) {
      setError(errorMessage(err, 'Failed to load preferences'));
      setLoading(false);
    }
  };
  
  const handleToggle = (field) => {
    setPreferences(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };
  
  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      // Build update object with only the preference fields
      const updates = {};
      const prefFields = [
        'welcome_emails', 'offer_reminders', 'redemption_confirmations',
        'business_welcome', 'business_daily_digest', 'subscription_updates',
        'venue_welcome', 'application_updates', 'coaster_alerts', 'inventory_alerts',
        'admin_daily_digest', 'admin_alerts', 'unsubscribe_all'
      ];
      
      prefFields.forEach(field => {
        if (preferences[field] !== undefined) {
          updates[field] = preferences[field];
        }
      });
      
      await api.put(`/email-preferences/${encodeURIComponent(token)}`, updates);
      clearUrlSecret('token');
      setSuccess('Preferences saved successfully!');
      setSaving(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(errorMessage(err, 'Failed to save preferences'));
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading preferences...</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (error && !preferences) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }
  
  const { user_type, email } = preferences;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-purple-50 to-pink-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-6">
            <img src="/tappd-logo.png" alt="Tappd" className="h-12 mb-4" />
            <h1 className="text-3xl font-bold text-white">Email Preferences</h1>
            <p className="text-purple-100 mt-2">Manage your email subscriptions</p>
          </div>
          
          <div className="px-8 py-6">
            {/* Email Display */}
            <div className="bg-gray-50 rounded-lg px-4 py-3 mb-6">
              <p className="text-sm text-gray-600">Managing preferences for:</p>
              <p className="text-lg font-semibold text-gray-900">{email}</p>
            </div>
            
            {/* Success Message */}
            {success && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center">
                <svg className="h-5 w-5 text-green-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-green-800">{success}</p>
              </div>
            )}
            
            {/* Error Message */}
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center">
                <svg className="h-5 w-5 text-red-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <p className="text-red-800">{error}</p>
              </div>
            )}
            
            {/* Consumer Preferences */}
            {user_type === 'consumer' && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="text-2xl mr-3">📧</span>
                  Marketing & Promotional Emails
                </h2>
                <div className="space-y-4">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.welcome_emails}
                      onChange={() => handleToggle('welcome_emails')}
                      className="mt-1 h-5 w-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">Welcome Emails</p>
                      <p className="text-sm text-gray-600">Receive welcome messages when you join Tappd</p>
                    </div>
                  </label>
                  
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.offer_reminders}
                      onChange={() => handleToggle('offer_reminders')}
                      className="mt-1 h-5 w-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">Offer Reminders</p>
                      <p className="text-sm text-gray-600">Get reminders about deals you've claimed</p>
                    </div>
                  </label>
                  {/* PD-9: the redemption confirmation email is paused, so no toggle for it. */}
                </div>
              </div>
            )}
            
            {/* Business Owner Preferences */}
            {user_type === 'business' && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="text-2xl mr-3">🏢</span>
                  Business Communications
                </h2>
                <div className="space-y-4">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.business_welcome}
                      onChange={() => handleToggle('business_welcome')}
                      className="mt-1 h-5 w-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">Welcome Emails</p>
                      <p className="text-sm text-gray-600">Onboarding and welcome messages for new businesses</p>
                    </div>
                  </label>
                  
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.business_daily_digest}
                      onChange={() => handleToggle('business_daily_digest')}
                      className="mt-1 h-5 w-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">Daily Digest</p>
                      <p className="text-sm text-gray-600">Daily reports on your deals and performance</p>
                    </div>
                  </label>
                  
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.subscription_updates}
                      onChange={() => handleToggle('subscription_updates')}
                      className="mt-1 h-5 w-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">Subscription Updates</p>
                      <p className="text-sm text-gray-600">Updates about your subscription and billing</p>
                    </div>
                  </label>
                </div>
              </div>
            )}
            
            {/* Venue Partner Preferences */}
            {user_type === 'venue_partner' && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="text-2xl mr-3">🏪</span>
                  Venue Partner Communications
                </h2>
                <div className="space-y-4">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.venue_welcome}
                      onChange={() => handleToggle('venue_welcome')}
                      className="mt-1 h-5 w-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">Welcome Emails</p>
                      <p className="text-sm text-gray-600">Welcome messages and onboarding information</p>
                    </div>
                  </label>
                  
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.application_updates}
                      onChange={() => handleToggle('application_updates')}
                      className="mt-1 h-5 w-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">Application Updates</p>
                      <p className="text-sm text-gray-600">Updates about your venue application status</p>
                    </div>
                  </label>
                  
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.coaster_alerts}
                      onChange={() => handleToggle('coaster_alerts')}
                      className="mt-1 h-5 w-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">Coaster Design Alerts</p>
                      <p className="text-sm text-gray-600">Notifications about coaster design changes</p>
                    </div>
                  </label>
                  
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.inventory_alerts}
                      onChange={() => handleToggle('inventory_alerts')}
                      className="mt-1 h-5 w-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">Inventory Alerts</p>
                      <p className="text-sm text-gray-600">Low coaster inventory notifications</p>
                    </div>
                  </label>
                </div>
              </div>
            )}
            
            {/* Admin Preferences */}
            {user_type === 'admin' && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="text-2xl mr-3">⚙️</span>
                  Admin Communications
                </h2>
                <p className="text-sm text-gray-600 mb-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                  Admin emails are critical for platform management and cannot be disabled.
                </p>
                <div className="space-y-4 opacity-50">
                  <label className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled
                      className="mt-1 h-5 w-5 text-purple-600 rounded"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">Daily Platform Digest</p>
                      <p className="text-sm text-gray-600">Daily reports on platform activity and metrics</p>
                    </div>
                  </label>
                  
                  <label className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled
                      className="mt-1 h-5 w-5 text-purple-600 rounded"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">Critical Alerts</p>
                      <p className="text-sm text-gray-600">Important system alerts and notifications</p>
                    </div>
                  </label>
                </div>
              </div>
            )}
            
            {/* Unsubscribe All */}
            {user_type !== 'admin' && (
              <div className="mb-8 border-t border-gray-200 pt-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="text-2xl mr-3">⚠️</span>
                  Unsubscribe from All
                </h2>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-4">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.unsubscribe_all}
                      onChange={() => handleToggle('unsubscribe_all')}
                      className="mt-1 h-5 w-5 text-red-600 rounded focus:ring-2 focus:ring-red-500"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">Stop all non-transactional emails</p>
                      <p className="text-sm text-gray-600 mt-1">
                        You'll still receive critical security emails like password resets and email verifications.
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            )}
            
            {/* Save Button */}
            <div className="flex items-center justify-between pt-6 border-t border-gray-200">
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-3 px-6 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
            
            {/* Help Text */}
            <p className="text-center text-sm text-gray-500 mt-6">
              Need help? Contact us at{' '}
              <a href="mailto:hello@tappd.app" className="text-purple-600 hover:text-purple-700 underline">
                hello@tappd.app
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

